import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  api,
  insertSubjectIntoVideo,
  approveSubjectPreview,
} from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABEL = {
  queued: "Getting ready",
  loading_scene: "Loading the target scene",
  editing_scene: "Inserting your subject (Gemini Nano Banana)",
  preview_done: "Preview ready — review below",
  rendering_clip: "Generating motion clip with your subject",
  remuxing: "Stitching the new video",
  done: "Done",
};

// Realistic per-stage ETAs in seconds so the UI can show a meaningful
// "Xs of ~Ys" counter. Preview mode skips clip + remux.
const PREVIEW_ETA = { queued: 3, loading_scene: 5, editing_scene: 20 };
const FULL_ETA = {
  queued: 3,
  loading_scene: 5,
  editing_scene: 20,
  rendering_clip: 90,
  remuxing: 15,
};
const STAGE_ORDER = [
  "queued",
  "loading_scene",
  "editing_scene",
  "rendering_clip",
  "remuxing",
];

const sum = (o) => Object.values(o).reduce((a, b) => a + b, 0);

// Helper kept outside the component so React's purity linter doesn't
// flag Date.now() inside the render path. The ticker above guarantees
// the component re-renders each second so the value stays current.
function computeElapsedSec(startedAt) {
  if (!startedAt) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
}

export default function SubjectInsertCard({ jobId, doc, onChanged }) {
  const subjectInsert = doc?.subject_insert || null;
  const status = subjectInsert?.status;
  const stage = subjectInsert?.stage;
  const error = subjectInsert?.error;
  const newVideoUrl = subjectInsert?.video_url;
  const previewUrl = subjectInsert?.preview_url;
  const startedAt = subjectInsert?.started_at;
  const mode = subjectInsert?.mode;
  const isRunning = status === "running";
  const isPreviewReady = status === "preview_ready";

  // Pick the right ETA budget based on what's actually running. In
  // "finalize" mode we're already past Gemini, so only Veo + remux
  // matter (~105s). In "preview" we're only doing Gemini (~28s).
  const etaMap =
    mode === "preview"
      ? PREVIEW_ETA
      : mode === "finalize"
        ? { rendering_clip: 90, remuxing: 15 }
        : FULL_ETA;
  const totalEtaSec = sum(etaMap);

  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [position, setPosition] = useState("start");
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);

  // 1-second ticker so the elapsed counter is live (decoupled from the
  // 2s render-state polling above).
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const elapsedSec = computeElapsedSec(startedAt);
  const pct = Math.min(99, Math.round((elapsedSec / totalEtaSec) * 100));

  useEffect(() => {
    if (status === "complete") {
      toast.success("Subject inserted! The new video is ready below.");
    } else if (status === "preview_ready") {
      toast.success("Preview ready — review it below before rendering the full video.");
    } else if (status === "failed") {
      toast.error(error || "Subject insert failed");
    }
  }, [status, error]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Pick an image file (PNG / JPG)");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image too large (10 MB max)");
      return;
    }
    setPreview({ url: URL.createObjectURL(f), name: f.name, size: f.size });
  };

  const submit = async (chosenMode) => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("Upload a photo of your subject first");
      return;
    }
    setSubmitting(true);
    try {
      await insertSubjectIntoVideo(jobId, f, position, chosenMode);
      toast.message(
        chosenMode === "preview"
          ? "Previewing — Gemini is editing the scene (~30s)"
          : "Started — full render in progress",
        {
          description:
            chosenMode === "preview"
              ? "We'll show the still image to review before spending Veo credits."
              : "This usually takes 1–3 minutes.",
        },
      );
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start subject insert");
    } finally {
      setSubmitting(false);
    }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await approveSubjectPreview(jobId);
      toast.message("Approved — rendering the motion clip now", {
        description: "~90s for Veo, then ~15s to re-stitch the video.",
      });
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't approve preview");
    } finally {
      setApproving(false);
    }
  };

  const stageStepIdx = STAGE_ORDER.indexOf(stage);

  return (
    <div
      data-testid="av-subject-insert"
      className="mt-4 pt-4 border-t border-zinc-900"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-3 flex items-center gap-2">
        <span>★</span>
        Add your dog (or any subject) to this video
      </div>

      {/* Result video — show ABOVE the upload form once a new one exists */}
      {status === "complete" && newVideoUrl && (
        <div className="mb-4 border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-2">
            ✓ New version with your subject
          </div>
          <video
            data-testid="av-subject-video"
            controls
            src={`${API_BASE}${newVideoUrl}`}
            className="w-full bg-black rounded-sm aspect-video"
          />
          <a
            href={`${API_BASE}${newVideoUrl}`}
            download={`${jobId}_with_subject.mp4`}
            className="inline-block mt-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-emerald-400 text-black font-bold px-3 py-1.5 rounded-sm hover:bg-emerald-300 transition-colors"
            data-testid="av-subject-download"
          >
            ↓ Download new version
          </a>
        </div>
      )}

      {/* Preview-ready: show the still image + approve/retry buttons */}
      {isPreviewReady && previewUrl && (
        <div
          className="mb-4 border border-emerald-400/50 bg-emerald-400/5 rounded-sm p-3"
          data-testid="av-subject-preview-result"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-2 flex items-center gap-2">
            <span>★</span> Preview — does this look right?
          </div>
          <img
            data-testid="av-subject-preview-image"
            src={`${API_BASE}${previewUrl}`}
            alt="subject-inserted scene preview"
            className="w-full bg-black rounded-sm aspect-video object-cover"
          />
          <p className="text-[11px] text-zinc-400 mt-2 leading-snug">
            This is the still frame Gemini Nano Banana produced for scene
            {" "}{(subjectInsert?.target_scene_idx ?? 0) + 1}. If it looks
            right, click <b className="text-emerald-300">Approve &amp; render</b>
            {" "}— Veo will animate this exact frame (~90s) and we&apos;ll re-stitch
            the video. Cost so far: $0.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              data-testid="av-subject-approve"
              onClick={approve}
              disabled={approving}
              className="flex-1 font-mono text-[11px] uppercase tracking-[0.2em] bg-emerald-400 text-black font-bold px-3 py-2 rounded-sm hover:bg-emerald-300 transition-colors disabled:opacity-50"
            >
              {approving ? "Starting…" : "✓ Approve & render"}
            </button>
            <button
              type="button"
              data-testid="av-subject-retry-preview"
              onClick={() => submit("preview")}
              disabled={submitting || !preview}
              className="flex-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300 border border-zinc-700 px-3 py-2 rounded-sm hover:border-yellow-400 hover:text-yellow-400 transition-colors disabled:opacity-50"
              title={!preview ? "Re-upload a subject photo to retry" : ""}
            >
              ↻ Try another preview
            </button>
          </div>
        </div>
      )}

      {isRunning && (
        <div className="mb-4 border border-yellow-400/40 bg-yellow-400/5 rounded-sm p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
              ⟳ {mode === "preview" ? "Previewing" : "Working"} — please wait
            </div>
            <div className="font-mono text-[11px] text-yellow-300 tabular-nums">
              {elapsedSec}s / ~{totalEtaSec}s
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 bg-yellow-400/10 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-yellow-400 transition-all duration-700"
              style={{ width: `${pct}%` }}
              data-testid="av-subject-progress"
            />
          </div>
          <p className="text-xs text-zinc-300">
            <span className="text-yellow-200 font-mono text-[10px] uppercase tracking-[0.15em] mr-2">
              Step {stageStepIdx >= 0 ? stageStepIdx + 1 : 1}/{STAGE_ORDER.length}
            </span>
            {STAGE_LABEL[stage] || stage || "Working…"}
            {subjectInsert?.target_scene_idx !== undefined && (
              <> · scene {subjectInsert.target_scene_idx + 1}</>
            )}
          </p>
          {elapsedSec > totalEtaSec + 30 && (
            <p className="text-[11px] text-yellow-200 mt-2">
              Taking longer than usual — the GPU queue may be busy.
              You can wait or cancel below.
            </p>
          )}
          <button
            type="button"
            data-testid="av-subject-reset"
            onClick={async () => {
              try {
                await api.post(`/auto-video/${jobId}/insert-subject/reset`);
                toast.success("Cleared — you can retry now");
                if (onChanged) onChanged();
              } catch (err) {
                toast.error("Couldn't reset");
              }
            }}
            className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-red-300 underline"
          >
            Stuck? Cancel and reset
          </button>
        </div>
      )}

      {status === "failed" && (
        <div className="mb-4 border border-red-500/40 bg-red-500/5 rounded-sm p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-300 mb-1">
            ✕ Last attempt failed
          </div>
          <p className="text-xs text-zinc-300">{error}</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 items-start">
        {/* Upload */}
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">
            Subject photo (PNG/JPG · 10 MB max)
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onFile}
            disabled={isRunning}
            data-testid="av-subject-file-input"
            className="block w-full text-xs text-zinc-300 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:bg-yellow-400 file:text-black file:font-bold file:px-3 file:py-1.5 file:rounded-sm file:border-0 file:mr-3 file:cursor-pointer file:hover:bg-yellow-300 disabled:opacity-50"
          />
          {preview && (
            <div className="mt-2 flex items-center gap-2">
              <img
                src={preview.url}
                alt="subject preview"
                className="w-16 h-16 object-cover rounded-sm border border-zinc-700"
              />
              <div className="text-[11px] text-zinc-400 truncate">
                {preview.name}
                <br />
                {(preview.size / 1024).toFixed(0)} KB
              </div>
            </div>
          )}
        </div>

        {/* Position picker */}
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">
            Insert near
          </label>
          <div className="flex gap-2">
            {["start", "end"].map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`av-subject-pos-${p}`}
                onClick={() => setPosition(p)}
                disabled={isRunning}
                className={`flex-1 font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 rounded-sm border transition-colors disabled:opacity-50 ${
                  position === p
                    ? "bg-yellow-400 text-black font-bold border-yellow-400"
                    : "text-zinc-400 border-zinc-800 hover:border-yellow-400/60 hover:text-yellow-400"
                }`}
              >
                {p === "start" ? "Beginning" : "End"} of video
              </button>
            ))}
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
            Preview = Gemini still ($0, ~30s). Approve to spend Veo credits
            on the motion clip + remux (~$0.50, ~2 min).
          </p>
        </div>
      </div>

      {/* Two-button row: cheap preview vs. one-shot full render */}
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-2 mt-4">
        <button
          type="button"
          onClick={() => submit("preview")}
          disabled={isRunning || submitting || !preview}
          data-testid="av-subject-submit-preview"
          className="font-mono text-[11px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-4 py-2.5 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? "Starting…" : "★ Preview edit ($0 · ~30s)"}
        </button>
        <button
          type="button"
          onClick={() => submit("full")}
          disabled={isRunning || submitting || !preview}
          data-testid="av-subject-submit-full"
          className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300 border border-zinc-700 px-4 py-2.5 rounded-sm hover:border-yellow-400 hover:text-yellow-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Skip the preview step and render the full video in one shot"
        >
          Skip preview → render
        </button>
      </div>
    </div>
  );
}
